import { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Helmet from '../lib/Helmet';
import { findBySlug, runningCost, siblingsByCapacity } from '../data/catalogue';
import { findCollection } from '../data/collections';
import { seriesInfo } from '../data/highlights';
import ProductFaq from '../components/ProductFaq';
import {
  PRICES, STOCK, RATES, KIT_COPPER_METRES, DELIVERY, PAYMENT, BUSINESS, EXCHANGE,
} from '../data/pricing';
import { FREE_ABOVE } from '../data/pincodes';
import { formatINR, modelWhatsAppLink, whatsAppLink } from '../lib/quote';
import { addToCart, openCart } from '../lib/cart';
import { onlineKit, isSoldOut, nextPriceFor, leadTimeFor } from '../lib/listing';
import DeliveryCheck from '../components/DeliveryCheck';
import ProductSchema from '../components/ProductSchema';
import ProductGallery from '../components/ProductGallery';
import { track, FUNNEL } from '../lib/track';
import WelcomeOffer from '../components/WelcomeOffer';
import ProductReviews, { ReviewSummary } from '../components/ProductReviews';
import HelpChoosing, { LIVE_GUIDE_SLUGS } from '../components/HelpChoosing';
import { helpForModel } from '../data/helpChoosing';

// One page per model. Routes are declared with static paths generated from
// CATALOGUE, so useParams() is empty here — the slug comes from the prop, then
// params, then the pathname.
//
// THIS PAGE SELLS ONE PRODUCT (Oct 2026). Someone who has opened a product
// wants to know more about it, not to be handed every other model again. So:
//
//   - No star-rating or series switcher. The only choice is size, because a
//     different size of the same AC is still this product.
//   - No piping-kit choice. Where a model exists with and without the kit, the
//     without-kit unit is the one sold online (see onlineKit in lib/listing.js)
//     and the kit is offered "at a discounted price on request" beside the
//     exchange offer. Kit-only series say the kit is included.
//   - No quantity stepper. Quantity changes in the cart.
//   - Model codes live in Specifications, not above the price.
//   - Out of stock is the same full page. Only two things change: the stock
//     line, and one "Request this AC" button in place of Add to cart / Buy now.
//
// Order of the buying column, top to bottom: name, price, why buy from us,
// size, delivery, why people pick this AC, offers, buttons.
//
// WARRANTY follows Daikin India's warranty card, which treats the two types
// differently. Inverter: 12 months on the unit, then 4 more years on the PCB
// and 9 more on the compressor (1 / 5 / 10 years). Non-inverter (GTQ): 12
// months on the unit, then 4 more years on the compressor only (1 / 5 years).
// ProductFaq.jsx must say the same.

const ICONS = {
  heat: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  bolt: <path d="M13 2L4 14h7l-1 8 9-12h-7z" />,
  wave: <path d="M3 12h4l3-8 4 16 3-8h4" />,
  drop: <path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z" />,
  filter: (
    <>
      <path d="M3 8h13a3 3 0 1 0-3-3" />
      <path d="M3 12h17a3 3 0 1 1-3 3" />
      <path d="M3 16h8" />
    </>
  ),
  carbon: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M8 9h8M8 12h8M8 15h8" />
    </>
  ),
  chill: <path d="M12 2v20M4.9 7l14.2 10M4.9 17L19.1 7" />,
  leaf: (
    <>
      <path d="M5 19c0-8 5-13 14-14-1 9-6 14-14 14z" />
      <path d="M5 19l7-7" />
    </>
  ),
  wifi: (
    <>
      <path d="M2 9a15 15 0 0 1 20 0" />
      <path d="M5 12.5a10 10 0 0 1 14 0" />
      <path d="M8.5 16a5 5 0 0 1 7 0" />
      <circle cx="12" cy="19.5" r="0.8" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  thermo: <path d="M14 14.8V5a2 2 0 0 0-4 0v9.8a4 4 0 1 0 4 0z" />,
  gauge: (
    <>
      <path d="M4 18a8 8 0 1 1 16 0" />
      <path d="M12 18l4-6" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3l8 3v6c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V6z" />
      <path d="M8.5 12l2.5 2.5 4.5-5" />
    </>
  ),
  store: (
    <>
      <path d="M3 10l9-6 9 6" />
      <path d="M5 10v10h14V10" />
      <path d="M10 20v-5h4v5" />
    </>
  ),
  truck: (
    <>
      <path d="M3 7h11v9H3z" />
      <path d="M14 10h4l3 3v3h-7z" />
      <circle cx="7" cy="18" r="1.8" />
      <circle cx="17" cy="18" r="1.8" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  exchange: (
    <>
      <path d="M4 9a8 8 0 0 1 14-3l2 2" />
      <path d="M20 4v4h-4" />
      <path d="M20 15a8 8 0 0 1-14 3l-2-2" />
      <path d="M4 20v-4h4" />
    </>
  ),
  pipe: (
    <>
      <path d="M3 7h9a5 5 0 0 1 5 5v9" />
      <path d="M3 11h9a1 1 0 0 1 1 1v9" />
    </>
  ),
  wrench: <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.5-.5-.5-2.5z" />,
  chat: <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />,
  check: <path d="M5 12l5 5L20 7" />,
  chevron: <path d="M9 6l6 6-6 6" />,
};

export function Icon({ name, className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" className={`shrink-0 ${className}`} fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name] || ICONS.check}
    </svg>
  );
}

export function Accordion({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
        className="flex w-full items-center justify-between py-3.5 text-left text-sm font-medium text-gray-900">
        {title}
        <svg viewBox="0 0 20 20" className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && <div className="pb-4 text-sm leading-relaxed text-gray-600">{children}</div>}
    </div>
  );
}

// One line in the offers box. With href it is a link and gets a chevron.
export function OfferRow({ icon, title, text, href, onClick, iconClass = 'text-gray-900' }) {
  const body = (
    <>
      <Icon name={icon} className={`mt-0.5 h-4 w-4 ${iconClass}`} />
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold leading-snug text-gray-900">{title}</span>
        <span className="block text-xs leading-snug text-gray-600">{text}</span>
      </span>
      {href && <Icon name="chevron" className="mt-0.5 h-4 w-4 text-gray-400" />}
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}
        className="flex items-start gap-2.5 px-3 py-2.5">
        {body}
      </a>
    );
  }
  return <div className="flex items-start gap-2.5 px-3 py-2.5">{body}</div>;
}

export default function ProductDetail({ slug: slugProp }) {
  const params = useParams();
  const { pathname } = useLocation();
  const slug = slugProp || params.slug || pathname.replace(/^\/+|\/+$/g, '');
  const model = findBySlug(slug);
  const [added, setAdded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (model) track(FUNNEL.PRODUCT_VIEW, { model: model.id });
  }, [model]);

  if (!model) return <Navigate to="/daikin-ac-price-list-delhi" replace />;

  // The one unit this page sells. Must match defaultKit() in
  // scripts/product-offer.js, or the pre-rendered page and the live page
  // would show different prices.
  const activeKit = onlineKit(model);
  const variant = model.variants[activeKit];
  const price = PRICES[model.id][activeKit];
  const save = variant.mrp - price;
  const inStock = ((STOCK[model.id] || {})[activeKit] || 0) > 0;
  // Every unit we held sold on the site. Same page, same Request button; only
  // the words change from "out of stock" to "sold out".
  const soldOut = isSoldOut(model.id, activeKit);
  const unitsLeft = (STOCK[model.id] || {})[activeKit] || 0;
  // Approved Oct 2026: a "price ladder" under the price when this unit is
  // sold below what the next one costs (NEXT_PRICE in data/pricing.js).
  const nextPrice = nextPriceFor(model.id, activeKit);
  const kitIncluded = activeKit === 'withKit';
  // 5 to 7 days, or sooner while NEXT_BATCH in data/pricing.js says so.
  const leadTime = leadTimeFor(model.id, activeKit);

  const info = seriesInfo(model);
  const tiles = [
    info.heat && {
      icon: 'heat',
      title: `Cools up to ${info.heat.max}\u00B0C outside`,
      text: `Full cooling power at ${info.heat.full}\u00B0C, so it keeps up through May and June.`,
      hot: true,
    },
    ...info.highlights,
  ].filter(Boolean).slice(0, 4);

  // Same series, other capacities. Each is its own page, so these are links.
  const sizes = siblingsByCapacity(model)
    .map((m) => {
      const k = onlineKit(m);
      const p = PRICES[m.id]?.[k];
      if (p == null) return null;
      return { m, price: p, inStock: ((STOCK[m.id] || {})[k] || 0) > 0, soldOut: isSoldOut(m.id, k) };
    })
    .filter(Boolean);

  const run = runningCost(model, { hoursPerYear: 1600, tariff: 5.2 });
  const tonCollection = findCollection(`daikin-${model.soldAs.replace('.', '-')}-ton-ac-price`);
  const bestExchange = EXCHANGE?.enabled && Array.isArray(EXCHANGE.copper) && EXCHANGE.copper.length
    ? Math.max(...EXCHANGE.copper.map((c) => c.value))
    : 0;
  const kitLink = whatsAppLink(
    `Hi, I would like the price of the original Daikin copper piping kit for the ${model.title} (${variant.idu}) on citicool.in.`,
  );

  // Combined model code label for the title tag only: withKit first,
  // e.g. FTKM50XV16MKA / GTKM50XV16MKA. Matches prerender.js.
  const withKitCode = model.variants.withKit?.idu;
  const withoutKitCode = model.variants.withoutKit?.idu;
  const codeLabel = [withKitCode, withoutKitCode].filter(Boolean).join(' / ');
  const typeLabel = model.inverter ? 'Inverter' : 'Fixed Speed';
  const pageTitle = `${model.title} Price ${formatINR(price)} (${codeLabel}) | Citi Cool`;
  const pageDescription = `${model.title} at ${formatINR(price)} (MRP ${formatINR(variant.mrp)}). ${inStock ? 'In stock, dispatched within 24 hours' : `Dispatched in ${leadTime}`}, Delhi NCR. Model ${codeLabel}.`;

  const handleAdd = (thenGo) => {
    // Not in the godown: this is a request for order, not a purchase.
    if (!inStock) {
      navigate(`/request-unit?model=${model.slug}&kit=${activeKit}`);
      return;
    }
    addToCart(model.id, activeKit, 1);
    track(FUNNEL.ADD_TO_CART, { model: model.id, kit: activeKit, qty: 1, value: price });
    if (thenGo) {
      track(FUNNEL.CHECKOUT_STARTED, { value: price, units: 1, source: 'buy_now' });
      navigate('/checkout');
      return;
    }
    openCart();
    setAdded(true);
    window.setTimeout(() => setAdded(false), 2500);
  };

  const whatsAppHref = modelWhatsAppLink(model, activeKit);
  const onWhatsApp = (source) => () => track(FUNNEL.WHATSAPP_HANDOFF, { model: model.id, source });

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description"
          content={pageDescription} />
      </Helmet>
      <ProductSchema model={model} variant={variant} price={price} inStock={inStock} />

      <main className="mx-auto w-full max-w-5xl overflow-x-hidden px-4 pt-24 pb-28 md:overflow-x-clip lg:pb-16 lg:pt-28 xl:max-w-6xl">
        <nav className="flex flex-wrap items-center gap-x-1 text-xs text-gray-500">
          <Link to="/daikin-ac-price-list-delhi" className="hover:text-blue-600">Daikin AC prices</Link>
          <span className="mx-1.5">›</span>
          {tonCollection ? (
            <Link to={`/${tonCollection.slug}`} className="hover:text-blue-600">{model.soldAs} Ton</Link>
          ) : (
            <span>{model.soldAs} Ton</span>
          )}
          <span className="mx-1.5">›</span>
          <span className="text-gray-700">{model.series} series</span>
        </nav>

        {/* Desktop (Clarity, Oct 2026): a bigger photo that stays in view
            while the buying column scrolls, with hover zoom. */}
        <div className="mt-4 grid w-full min-w-0 max-w-full gap-6 md:grid-cols-[minmax(0,340px)_minmax(0,1fr)] md:gap-8 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)] xl:grid-cols-[minmax(0,500px)_minmax(0,1fr)]">
          <div className="min-w-0 md:sticky md:top-24 md:z-20 md:self-start">
            <ProductGallery model={model} />
          </div>

          <div className="min-w-0">
            {/* Name */}
            <p className="text-xs font-semibold text-blue-700">Daikin · {model.series} series</p>
            <h1 className="mt-0.5 break-words text-[17px] font-semibold leading-snug text-gray-900 sm:text-xl">{model.title}</h1>
            {info.tagline && (
              <p className="mt-1 text-[13px] leading-snug text-gray-600">{info.tagline}</p>
            )}
            <ReviewSummary slug={model.slug} />

            {/* Price */}
            <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-2xl font-bold leading-none text-gray-900">{formatINR(price)}</span>
              {soldOut && (
                <span className="rounded border-2 border-red-600 px-1.5 py-0.5 text-xs font-black uppercase tracking-wide text-red-600">
                  Sold out
                </span>
              )}
              {save > 0 && (
                <>
                  <span className="text-sm text-gray-500 line-through">{formatINR(variant.mrp)}</span>
                  <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-800">
                    Save {formatINR(save)}
                  </span>
                </>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-600">
              Inclusive of GST · <span className="font-semibold text-gray-900">EMI available at checkout</span>
            </p>

            {/* Price ladder: this unit at the old-stock price, the next at its real cost */}
            {nextPrice && (
              <section aria-label="Price for this unit and the next"
                className="mt-3 flex flex-col gap-2 rounded-[10px] border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-red-700">
                    {unitsLeft === 1 ? 'Last unit at this price' : `Only ${unitsLeft} left at this price`}
                  </p>
                  <span className="shrink-0 rounded-full bg-red-700 px-2 py-0.5 text-[11px] font-bold text-white">{unitsLeft} left</span>
                </div>
                <div className="flex items-center gap-2.5 rounded-lg border-2 border-amber-600 bg-white p-2.5">
                  <span aria-hidden="true" className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 border-amber-600">
                    <span className="h-2 w-2 rounded-full bg-amber-600" />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="text-[13px] font-semibold text-gray-900">{unitsLeft === 1 ? 'This unit' : 'These units'}</span>
                    <span className="text-[11px] text-gray-600">In our godown · delivered tomorrow</span>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-[15px] font-bold text-gray-900">{formatINR(price)}</span>
                    <span className="text-[11px] font-semibold text-green-700">{formatINR(nextPrice - price)} less</span>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 rounded-lg border border-dashed border-stone-300 p-2.5">
                  <span aria-hidden="true" className="h-4 w-4 shrink-0 rounded-full border-2 border-stone-400" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="text-[13px] font-semibold text-stone-600">Next unit</span>
                    <span className="text-[11px] text-stone-600">Ordered from Daikin · {leadTime}</span>
                  </div>
                  <span className="text-[15px] font-bold text-stone-600">{formatINR(nextPrice)}</span>
                </div>
                <p className="text-[11px] leading-snug text-stone-600">
                  This is our old-stock price. Once {unitsLeft === 1 ? 'this unit sells' : 'these units sell'}, the price goes back to {formatINR(nextPrice)}.
                </p>
              </section>
            )}

            {/* Why buy from us: one slim strip, two points side by side */}
            <div className="mt-3 grid grid-cols-2 divide-x divide-blue-100 rounded-lg bg-blue-50">
              <div className="flex items-start gap-2 px-2.5 py-2">
                <Icon name="store" className="mt-0.5 h-4 w-4 text-blue-700" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold leading-snug text-blue-900">Sales and service under one roof</p>
                  <p className="text-[11px] leading-snug text-blue-800">No running around</p>
                </div>
              </div>
              <div className="flex items-start gap-2 px-2.5 py-2">
                <Icon name="shield" className="mt-0.5 h-4 w-4 text-blue-700" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold leading-snug text-blue-900">Authorised Daikin dealer since 2003</p>
                  <p className="text-[11px] leading-snug text-blue-800">Cooling Delhi since 1990</p>
                </div>
              </div>
            </div>

            {/* Size: other capacities of this same AC */}
            {sizes.length > 1 && (
              <div className="mt-4">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-[13px] font-semibold text-gray-900">Size</p>
                  <Link to="/ac-calculator" className="text-xs text-blue-700 hover:underline">AC size calculator</Link>
                </div>
                <div className="mt-1.5 grid grid-cols-3 gap-1.5 sm:grid-cols-4">
                  {sizes.map(({ m, price: p, inStock: s, soldOut: so }) => {
                    const current = m.id === model.id;
                    return (
                      <Link key={m.id} to={`/${m.slug}`} aria-current={current ? 'page' : undefined}
                        className={`flex min-h-[44px] flex-col items-center justify-center rounded-md px-1 py-1.5 text-center transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
                          current ? 'border-2 border-blue-700 bg-blue-50' : 'border border-gray-300 bg-white'
                        }`}>
                        <span className={`text-[13px] font-semibold leading-tight ${current ? 'text-blue-700' : 'text-gray-900'}`}>{m.soldAs} Ton</span>
                        <span className="text-[11px] leading-tight text-gray-600">{formatINR(p)}</span>
                        <span className={`text-[10px] leading-tight ${s ? 'font-semibold text-green-700' : so ? 'font-semibold text-red-700' : 'text-gray-500'}`}>{s ? 'In stock' : so ? 'Sold out' : 'On order'}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Delivery */}
            <div className="mt-4">
              {inStock ? (
                <p className="flex items-center gap-1.5 text-[13px] font-semibold text-green-700">
                  <Icon name="truck" className="h-4 w-4" />
                  In stock · {DELIVERY.badge}
                </p>
              ) : (
                <div className="flex items-start gap-1.5">
                  <Icon name="clock" className="mt-0.5 h-4 w-4 text-amber-700" />
                  <div>
                    <p className="text-[13px] font-semibold text-amber-800">
                      {soldOut ? 'Sold out online' : 'Out of stock'} · we order it from Daikin for you
                    </p>
                    <p className="text-xs text-gray-600">Delivered in {leadTime} after we confirm your order.</p>
                  </div>
                </div>
              )}
              <DeliveryCheck price={price} qty={1} inStock={inStock} leadTime={leadTime} />
              <p className="mt-1.5 text-[11px] leading-relaxed text-gray-500">
                Delhi, Gurgaon, Noida, Faridabad and Ghaziabad. Charge includes GST, nothing added later.
                Free delivery above {formatINR(FREE_ABOVE)}.
              </p>
            </div>

            {/* Why people pick this AC */}
            {tiles.length > 0 && (
              <section className="mt-5">
                <h2 className="text-[15px] font-semibold text-gray-900">Why people pick this AC</h2>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {tiles.map((t) => (
                    <div key={t.title} className="flex flex-col gap-1 rounded-lg bg-gray-50 p-2.5">
                      <Icon name={t.icon} className={`h-[18px] w-[18px] ${t.hot ? 'text-orange-700' : 'text-blue-700'}`} />
                      <p className="text-[13px] font-semibold leading-snug text-gray-900">{t.title}</p>
                      {t.text && <p className="text-xs leading-snug text-gray-600">{t.text}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Offers */}
            <div className="mt-5 divide-y divide-gray-200 rounded-lg border border-gray-200">
              {bestExchange > 0 && (
                <OfferRow icon="exchange"
                  title={`Up to ${formatINR(bestExchange)} off your old AC`}
                  text="Any brand · free uninstallation · applied at checkout" />
              )}
              {kitIncluded ? (
                <OfferRow icon="check" iconClass="text-green-700"
                  title="Copper piping kit included"
                  text={`This model ships with Daikin's ${KIT_COPPER_METRES} m kit in the box.`} />
              ) : (
                <OfferRow icon="pipe" href={kitLink} onClick={onWhatsApp('kit_request')}
                  title="Copper piping kit at a discounted price"
                  text="On request. Ask us on WhatsApp, or when we call to confirm delivery." />
              )}
              <OfferRow icon="wrench"
                title="Installation, quoted before we start"
                text="Say yes when you order. We visit, give you a fixed price, then fit it." />
            </div>

            {/* Buttons */}
            <div className="mt-5">
              {inStock ? (
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => handleAdd(false)}
                    className="h-12 rounded-lg border-2 border-blue-700 bg-white text-[15px] font-semibold text-blue-700 transition hover:bg-blue-50">
                    {added ? 'Added to cart' : 'Add to cart'}
                  </button>
                  <button type="button" onClick={() => handleAdd(true)}
                    className="h-12 rounded-lg bg-blue-700 text-[15px] font-semibold text-white transition hover:bg-blue-800">
                    Buy now
                  </button>
                </div>
              ) : (
                <>
                  <button type="button" onClick={() => handleAdd(false)}
                    className="h-12 w-full rounded-lg bg-blue-700 text-[15px] font-semibold text-white transition hover:bg-blue-800">
                    Request this AC
                  </button>
                  <p className="mt-1 text-center text-xs text-gray-600">No payment now. We call you to confirm the delivery date.</p>
                </>
              )}
              <a href={whatsAppHref} target="_blank" rel="noopener noreferrer" onClick={onWhatsApp('product_page')}
                className="mt-1 flex h-10 items-center justify-center gap-1.5 text-sm font-semibold text-green-700 hover:text-green-800">
                <Icon name="chat" className="h-4 w-4" />
                Questions? Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>

        <div className="mt-10 w-full min-w-0 max-w-3xl">
          <Accordion title="Specifications" defaultOpen>
            <dl className="w-full min-w-0 divide-y divide-gray-100">
              {[
                ['Brand', 'Daikin'],
                ['Type', model.inverter ? 'Inverter split AC' : 'Fixed speed split AC'],
                ['Model', `${variant.idu} + ${variant.odu}`],
                ['Piping kit', kitIncluded ? `Included, ${KIT_COPPER_METRES} m` : 'Not included · available on request'],
                ['Rated cooling capacity', `${model.kw} kW`],
                ['Sold as', `${model.soldAs} ton`],
                model.coverageSqft && ['Coverage area', model.coverageSqft],
                ['BEE star rating', `${model.stars} star`],
                ['ISEER', model.iseer],
                ['Annual power consumption', `${model.annualKwh} kWh at 1,600 hours`],
                info.heat && ['Cools in outdoor heat', `Up to ${info.heat.max}\u00B0C · full power at ${info.heat.full}\u00B0C`],
                info.heatingMin != null && ['Heating', `Down to ${info.heatingMin}\u00B0C outside`],
                model.compressor && ['Compressor', model.compressor],
                model.airflowCfm && ['Airflow rate', `${model.airflowCfm} CFM`],
                model.noiseDb && ['Noise level', `${model.noiseDb} dB`],
                ['Refrigerant', 'R32'],
                ['Condenser', '100% copper'],
                model.dimsIdu && ['Indoor unit dimensions', model.dimsIdu],
                model.dimsOdu && ['Outdoor unit dimensions', model.dimsOdu],
                ['Warranty', model.inverter
                  ? '1 year comprehensive \u00B7 10 year compressor \u00B7 5 year PCB'
                  : '1 year comprehensive \u00B7 5 year compressor'],
                ['Country of origin', 'India'],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 py-2">
                  <dt className="text-gray-500">{k}</dt>
                  <dd className="min-w-0 break-words text-right text-gray-900">{v}</dd>
                </div>
              ))}
            </dl>
          </Accordion>

          <Accordion title="Running cost">
            <p>
              About {run.kwh} units a year at 1,600 hours of use — roughly {formatINR(run.cost)} on a
              Delhi tariff of ₹{run.tariff} a unit. Noida and Ghaziabad tariffs run higher, so budget
              closer to {formatINR(Math.round(run.kwh * 9.7))} there.
            </p>
            <p className="mt-2.5 text-gray-500">
              BEE's rated figure. Your actual bill depends on hours, thermostat setting and how
              exposed the room is.
            </p>
          </Accordion>

          <Accordion title="Installation rates">
            <p className="mb-2 text-gray-500">
              Our published rates, before GST. If you want us to install, we visit, measure and give you
              a fixed quote at these rates before any work starts.
            </p>
            <dl className="w-full min-w-0 divide-y divide-gray-100">
              {[RATES.installSplit, RATES.nitrogenTest, RATES.copperSplit, RATES.drainPipe,
                RATES.controlWiring, RATES.standSplit1, RATES.standSplit15, RATES.dismantleOld,
                RATES.antiCorrosion, RATES.chiseling].map((r) => (
                <div key={r.label} className="flex justify-between gap-4 py-2">
                  <dt className="text-gray-500">{r.label}</dt>
                  <dd className="shrink-0 text-gray-900">
                    {formatINR(r.price)} <span className="text-gray-400">/ {r.per}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </Accordion>

          <Accordion title="Payment and delivery">
            <p>Equipment: {PAYMENT.equipment}.</p>
            <p className="mt-1">Installation: {PAYMENT.installation}.</p>
            <p className="mt-2.5 text-gray-500">{DELIVERY.footnote}</p>
            <p className="mt-2.5 text-gray-500">
              We install across {BUSINESS.serviceAreas.join(', ')}. GSTIN {BUSINESS.gstin}.
            </p>
          </Accordion>
        </div>

        <HelpChoosing links={helpForModel(model, LIVE_GUIDE_SLUGS)} className="mt-10 max-w-3xl" />

        <ProductFaq model={model} price={price} />

        <ProductReviews slug={model.slug} productName={model.title} />

        {/* The one way out to other models, at the very end. */}
        <Link to={tonCollection ? `/${tonCollection.slug}` : '/daikin-ac-price-list-delhi'}
          className="mt-10 flex max-w-3xl items-center justify-between gap-3 rounded-xl border border-gray-200 p-4 transition hover:border-blue-400">
          <span>
            <span className="block text-[15px] font-semibold text-gray-900">Looking for 5 Star, WiFi or heating?</span>
            <span className="mt-0.5 block text-[13px] text-gray-600">
              {tonCollection ? `See all ${model.soldAs} Ton ACs` : 'See all Daikin ACs'}
            </span>
          </span>
          <Icon name="chevron" className="h-5 w-5 text-blue-700" />
        </Link>

        {/* Sticky buy bar on phones: price and buying only. Help (call and
            WhatsApp) sits quietly in the header on shop pages, and the
            floating buttons are hidden here (lib/productPath.js). */}
        <div className="fixed inset-x-0 bottom-0 z-50 flex items-center gap-2.5 border-t border-gray-200 bg-white pl-4 pr-3 pb-[max(0.625rem,env(safe-area-inset-bottom))] pt-2.5 shadow-[0_-2px_12px_rgba(0,0,0,0.08)] lg:hidden">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[11px] text-gray-600">
              {model.soldAs} Ton · {model.stars} Star
              {nextPrice && unitsLeft === 1 && <> · <span className="font-semibold text-red-700">last unit</span></>}
            </p>
            <p className="text-base font-bold text-gray-900">{formatINR(price)}</p>
          </div>
          {inStock ? (
            <>
              <button type="button" onClick={() => handleAdd(false)}
                className="h-11 shrink-0 rounded-xl border-2 border-blue-700 px-3 text-sm font-semibold text-blue-700">
                {added ? 'Added' : 'Add'}
              </button>
              <button type="button" onClick={() => handleAdd(true)}
                className="h-11 shrink-0 rounded-xl bg-blue-700 px-4 text-sm font-semibold text-white">
                Buy now
              </button>
            </>
          ) : (
            <button type="button" onClick={() => handleAdd(false)}
              className="h-11 shrink-0 rounded-xl bg-blue-700 px-4 text-sm font-semibold text-white">
              Request this AC
            </button>
          )}
        </div>
      </main>
      <WelcomeOffer />
    </>
  );
}